/**
 * Configuration for reconstructing the "Steps Completed by Agent" tracker from
 * RPA audit-trail data.
 *
 * The tracker renders every granular RPA step verbatim (no clubbing, grouping,
 * or phasing), so this module only supplies:
 * - a neutral marker icon shared by every rendered step,
 * - the `submission_type` -> `rpa_name` mapping used to locate the submission
 *   bot's logs, and
 * - the follow-up action items shown when a submission stops at a saved draft.
 *
 * @module submissionAgentStepsConfig
 */
import { AgentActionItem } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";

/**
 * Small stroke-based icon wrapper inheriting its color from the tracker marker.
 *
 * @input props - SVG path children rendered inside a 24x24 viewport.
 * @returns The icon element.
 */
const StepIcon = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/** Neutral marker icon rendered for every completed raw RPA step. */
export const GENERIC_STEP_ICON: React.ReactNode = (
  <StepIcon>
    <polyline points="20 6 9 17 4 12" />
  </StepIcon>
);

/** Marker icon rendered for a step that needs the user's attention. */
export const ALERT_STEP_ICON: React.ReactNode = (
  <StepIcon>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </StepIcon>
);

/**
 * Maps `workflow_config.submission_type` to the `rpa_name` the submission bot
 * writes to `analytics.rpa_action_logs`.
 */
export const SUBMISSION_TYPE_TO_RPA_NAME: Record<string, string> = {
  rpa_carelon: "carelon_pa_submission",
  rpa_evicore: "evicore",
  rpa_nch: "new_century",
  rpa_bcbs: "bcbs_flora",
  rpa_healthfirst: "availity_healthfirst_pa_submission",
  rpa_ilinkblue: "ilinkblue",
  aetna: "aetna_pa_submission",
};

/**
 * Follow-up items shown under the action-required step for a saved draft.
 *
 * The login item intentionally omits `href`; the real payer-portal URL is
 * injected at build time when available, so no link renders when it is null.
 */
export const DRAFT_ACTION_ITEMS: AgentActionItem[] = [
  {
    id: "draft-login",
    text: "Log in to payer portal",
    linkAriaLabel: "Open payer portal",
  },
  {
    id: "draft-complete",
    text: "Complete the further actions to submit the auth",
  },
  { id: "draft-mark", text: "Mark as Submitted" },
];

/**
 * Follow-up items shown under the failing step when a submission fails.
 *
 * The login item intentionally omits `href`; the real payer-portal URL is
 * injected at build time when available, so no link renders when it is null.
 */
export const FAILED_ACTION_ITEMS: AgentActionItem[] = [
  {
    id: "failed-login",
    text: "Log in to payer portal",
    linkAriaLabel: "Open payer portal",
  },
  {
    id: "failed-complete",
    text: "Complete the submission manually from this step",
  },
  { id: "failed-mark", text: "Mark as Submitted" },
];

/**
 * Resolves the submission bot's `rpa_name` for an order.
 *
 * Maps `workflow_config.submission_type` directly to the `rpa_name` the
 * submission bot writes to `analytics.rpa_action_logs`.
 *
 * @input submissionType - `workflow_config.submission_type` for the order.
 * @returns The resolved `rpa_name`, or undefined when the type is unknown.
 */
export const resolveSubmissionRpaName = (
  submissionType: string | undefined,
): string | undefined =>
  submissionType ? SUBMISSION_TYPE_TO_RPA_NAME[submissionType] : undefined;
